"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Vue = require("vue");
const Client_1 = require("../../api/Client");
const IDomChangeEvent_1 = require("@ulixee/hero-interfaces/IDomChangeEvent");
const DomNodeState_1 = require("./DomNodeState");
const DomNode_vue_1 = require("./DomNode.vue");
const DevtoolsMessenger_1 = require("../../lib/devtools/DevtoolsMessenger");
exports.default = Vue.defineComponent({
    name: 'StatePanel',
    components: { DomNode: DomNode_vue_1.default },
    setup() {
        return {
            focusedPaintIndex: Vue.ref(null),
            paintEvents: Vue.reactive([]),
            loadedIndexRange: Vue.ref([-1, -1]),
            domNodesByFrameId: Vue.reactive({}),
            framesById: Vue.reactive(new Map()),
            mainFrameIds: Vue.reactive(new Set()),
            activeGroupId: -1,
            latestGroupId: 0,
            hiddenNodeGroups: Vue.reactive({
                isExpandedByGroupId: new Map(),
                frameNodeIdsByGroupId: new Map(),
                collapsedGroupIdByFrameNodeId: new Map(),
                createdGroupIdByFrameNodeId: new Map(),
            }),
        };
    },
    watch: {},
    computed: {
        mainFrame() {
            for (const frameId of this.mainFrameIds) {
                if (frameId in this.domNodesByFrameId) {
                    return this.domNodesByFrameId[frameId];
                }
            }
        },
    },
    methods: {
        setPaintEvents(paintEvents, framesById) {
            this.paintEvents.length = paintEvents.length;
            for (let i = 0; i < paintEvents.length; i += 1) {
                // don't overwrite existing with empty
                if (paintEvents[i]?.length) {
                    this.paintEvents[i] = paintEvents[i];
                }
            }
            window.paintEvents = this.paintEvents;
            this.mainFrameIds.clear();
            this.framesById.clear();
            for (const [id, value] of Object.entries(framesById)) {
                if (!value.parentId)
                    this.mainFrameIds.add(Number(id));
                this.framesById.set(Number(id), value);
            }
            if (!this.focusedPaintIndex) {
                const index = paintEvents.length - 1;
                this.setPaintIndex([index, index]);
            }
        },
        setPaintIndex(loadedIndices, documentLoadPaintIndex = 0) {
            if (loadedIndices[0] === this.loadedIndexRange[0] &&
                loadedIndices[1] === this.loadedIndexRange[1])
                return;
            for (const frameId of Object.keys(this.domNodesByFrameId)) {
                delete this.domNodesByFrameId[frameId];
            }
            for (let i = documentLoadPaintIndex; i <= loadedIndices[1]; i += 1) {
                if (!this.paintEvents[i])
                    continue;
                const highlight = i >= loadedIndices[0] && i <= loadedIndices[1];
                this.applyDomChanges(this.paintEvents[i], highlight);
            }
            this.loadedIndexRange = loadedIndices;
            this.activeGroupId = 0;
            this.latestGroupId = 0;
            this.hiddenNodeGroups.frameNodeIdsByGroupId.clear();
            this.hiddenNodeGroups.isExpandedByGroupId.clear();
            this.hiddenNodeGroups.collapsedGroupIdByFrameNodeId.clear();
            this.hiddenNodeGroups.createdGroupIdByFrameNodeId.clear();
            this.findTreeChanges(this.mainFrame.document);
        },
        findTreeChanges(nodeState) {
            const groups = this.hiddenNodeGroups;
            if (nodeState.hasChanges) {
                this.activeGroupId = null;
            }
            else if (!nodeState.isDoctype &&
                !nodeState.isDocument &&
                nodeState.tagName !== 'html' &&
                !(nodeState.isTextNode && !nodeState.hasText)) {
                if (this.activeGroupId) {
                    groups.collapsedGroupIdByFrameNodeId.set(nodeState.frameNodeId, this.activeGroupId);
                    groups.frameNodeIdsByGroupId.get(this.activeGroupId).push(nodeState.frameNodeId);
                }
                else {
                    const nextId = (this.latestGroupId += 1);
                    this.activeGroupId = nextId;
                    groups.isExpandedByGroupId.set(nextId, false);
                    groups.createdGroupIdByFrameNodeId.set(nodeState.frameNodeId, nextId);
                    groups.collapsedGroupIdByFrameNodeId.set(nodeState.frameNodeId, nextId);
                    groups.frameNodeIdsByGroupId.set(this.activeGroupId, [nodeState.frameNodeId]);
                }
            }
            for (const child of nodeState.children) {
                this.findTreeChanges(child);
            }
            if (nodeState.contentDocument) {
                for (const child of nodeState.contentDocument.children) {
                    this.findTreeChanges(child);
                }
            }
        },
        applyDomChanges(changeEvents, highlight) {
            for (const change of changeEvents) {
                if (change.action === IDomChangeEvent_1.DomActionType.location)
                    continue;
                if (change.action === IDomChangeEvent_1.DomActionType.newDocument) {
                    this.domNodesByFrameId[change.frameId] = { nodesById: {} };
                    continue;
                }
                try {
                    this.domNodesByFrameId[change.frameId] ??= {
                        nodesById: {},
                    };
                    const frameContext = this.domNodesByFrameId[change.frameId];
                    frameContext.nodesById[change.nodeId] ??= new DomNodeState_1.default(change.frameId, frameContext.nodesById, change.nodeId);
                    const domNode = frameContext.nodesById[change.nodeId];
                    domNode.apply(change, highlight);
                    if (domNode.isDocument) {
                        frameContext.document = domNode;
                        const frame = this.framesById.get(change.frameId);
                        if (frame.parentId) {
                            const parentFrameContext = this.domNodesByFrameId[frame.parentId];
                            const containerFrameElement = parentFrameContext?.nodesById[frame.domNodeId];
                            if (!containerFrameElement) {
                                console.warn('Could not find dom node for frame', frame, change);
                            }
                            else {
                                containerFrameElement.contentDocument = domNode;
                            }
                        }
                    }
                }
                catch (error) {
                    console.error('ERROR apply dom change', { change, error });
                }
            }
        },
        onDomFocus(event) {
            this.focusedPaintIndexRange = event.highlightPaintIndexRange;
            this.setPaintIndex(event.highlightPaintIndexRange, event.documentLoadPaintIndex);
        },
        onDomUpdated(event) {
            this.setPaintEvents(event.paintEvents, event.framesById);
        },
        onDomResponse(response) {
            this.setPaintEvents(response.paintEvents.map(x => x.changeEvents), response.framesById);
            Client_1.default.on('Dom.updated', this.onDomUpdated);
            Client_1.default.on('Dom.focus', this.onDomFocus);
        },
    },
    mounted() {
        (0, DevtoolsMessenger_1.sendToBackgroundScript)({ action: 'getMinerAddress' }, address => {
            window.setMinerAddress(address);
            Client_1.default.send('Session.getDom')
                .then(this.onDomResponse)
                .catch(err => alert(err.stack));
        });
    },
    beforeUnmount() {
        Client_1.default.off('Dom.updated', this.onDomUpdated);
        Client_1.default.off('Dom.focus', this.onDomFocus);
    },
});
